import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Save, Rocket, ExternalLink, Globe, Palette, Upload, Wand2, Plus, Trash2, ArrowLeft, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import OxyHeader from "../components/OxyHeader";
import EcommerceManager from "../components/EcommerceManager";
import NewsManager from "../components/NewsManager";
import PhotoCard from "../components/PhotoCard";
import DemoPayment from "../components/DemoPayment";
import VisualBuilder from "../components/VisualBuilder";
import DomainWizard from "../components/DomainWizard";

export default function WebsiteEditor() {
  const { website_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [tab, setTab] = useState("content");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTask, setAiTask] = useState("content");
  const [aiWorking, setAiWorking] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    api.get(`/websites/${website_id}`).then(r => setW(r.data)).catch(() => navigate("/dashboard"));
  }, [website_id, user]);

  const save = async () => {
    await api.put(`/websites/${website_id}`, w);
    toast.success("Saved");
  };

  const publish = async () => {
    await api.post(`/websites/${website_id}/publish`);
    setW({ ...w, status: "published" });
    toast.success("Published! 🚀");
  };

  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setW(prev => ({ ...prev, [key]: r.data.path }));
    toast.success("Uploaded");
  };

  const runAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiWorking(true); setAiResult("");
    try {
      const r = await api.post("/ai/generate", { task: aiTask, prompt: aiPrompt, context: `Website: ${w.name}, Type: ${w.type}, Language: ${w.language}` });
      setAiResult(r.data.output);
    } catch { toast.error("AI failed"); } finally { setAiWorking(false); }
  };

  if (!w) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Link to="/dashboard" className="hover:text-sky-600"><ArrowLeft className="w-4 h-4 inline mr-1"/>My Websites</Link>
          <span>/</span>
          <span className="font-semibold text-slate-900">{w.name}</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-extrabold">{w.name}</h1>
              <Badge className="uppercase" variant="outline">{w.type}</Badge>
              <Badge className={w.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{w.status}</Badge>
            </div>
            <div className="text-xs text-slate-500 font-mono mt-1">{w.subdomain}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <DemoPayment website={w} onPaid={() => api.get(`/websites/${website_id}`).then(r => setW(r.data))}/>
            <Dialog open={aiOpen} onOpenChange={setAiOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="ai-assist-btn"><Wand2 className="w-4 h-4 mr-1.5 text-sky-500"/>AI Assist</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>🤖 Build It Your Way</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>What do you need?</Label>
                    <select value={aiTask} onChange={e => setAiTask(e.target.value)} className="w-full mt-1 h-9 rounded-md border border-slate-300 px-2 text-sm">
                      <option value="content">Generate content</option>
                      <option value="translate">Translate</option>
                      <option value="seo">SEO meta</option>
                      <option value="headline">News headline</option>
                      <option value="outline">Page outline</option>
                    </select>
                  </div>
                  <Textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Describe what you want..." rows={4} data-testid="ai-prompt"/>
                  <Button onClick={runAi} disabled={aiWorking} className="bg-sky-500 hover:bg-sky-600" data-testid="ai-run">
                    {aiWorking ? "Generating..." : "✨ Generate"}
                  </Button>
                  {aiResult && <div className="max-h-64 overflow-y-auto p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap font-mono border">{aiResult}</div>}
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={save} variant="outline" className="rounded-full" data-testid="save-website"><Save className="w-4 h-4 mr-1.5"/>Save</Button>
            <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full"><ExternalLink className="w-4 h-4 mr-1.5"/>Preview</Button></a>
            <Button onClick={publish} className="rounded-full bg-emerald-500 hover:bg-emerald-600" data-testid="publish-website"><Rocket className="w-4 h-4 mr-1.5"/>Publish</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-white border border-slate-200 h-auto p-1 flex-wrap">
            <TabsTrigger value="content" data-testid="tab-content"><FileText className="w-4 h-4 mr-1.5"/>Content</TabsTrigger>
            <TabsTrigger value="builder" data-testid="tab-builder">🎨 Builder</TabsTrigger>
            <TabsTrigger value="theme" data-testid="tab-theme"><Palette className="w-4 h-4 mr-1.5"/>Theme</TabsTrigger>
            {w.type === "ecommerce" && <TabsTrigger value="products" data-testid="tab-products">🛍 Products</TabsTrigger>}
            {(w.type === "news" || w.type === "blog") && <TabsTrigger value="articles" data-testid="tab-articles">📰 Articles</TabsTrigger>}
            {w.type === "ecommerce" && <TabsTrigger value="photocard" data-testid="tab-photocard"><ImageIcon className="w-4 h-4 mr-1.5"/>Photo Cards</TabsTrigger>}
            <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
            <TabsTrigger value="domain" data-testid="tab-domain"><Globe className="w-4 h-4 mr-1.5"/>Domain</TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            <div className="max-w-3xl bg-white p-6 rounded-xl border border-slate-200 space-y-4">
              <div><Label>Website name</Label><Input value={w.name} onChange={e => setW({...w, name: e.target.value})} data-testid="edit-name"/></div>
              <div>
                <Label>Logo</Label>
                <div className="flex items-center gap-3 mt-1">
                  {w.logo_url && <img src={fileUrl(w.logo_url)} className="w-14 h-14 rounded-lg object-cover border" alt=""/>}
                  <input ref={fileRef} type="file" hidden onChange={e => upload(e.target.files[0], "logo_url")}/>
                  <Button variant="outline" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1.5"/>Upload</Button>
                </div>
              </div>
              <div><Label>Hero Title</Label><Input value={w.content?.hero_title || ""} onChange={e => setW({...w, content: {...w.content, hero_title: e.target.value}})} data-testid="hero-title"/></div>
              <div><Label>Hero Subtitle</Label><Textarea value={w.content?.hero_subtitle || ""} onChange={e => setW({...w, content: {...w.content, hero_subtitle: e.target.value}})} rows={2}/></div>
              <div><Label>About</Label><Textarea value={w.content?.about || ""} onChange={e => setW({...w, content: {...w.content, about: e.target.value}})} rows={4}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact email</Label><Input value={w.content?.email || ""} onChange={e => setW({...w, content: {...w.content, email: e.target.value}})}/></div>
                <div><Label>Phone</Label><Input value={w.content?.phone || ""} onChange={e => setW({...w, content: {...w.content, phone: e.target.value}})}/></div>
              </div>
              <div><Label>Address</Label><Input value={w.content?.address || ""} onChange={e => setW({...w, content: {...w.content, address: e.target.value}})}/></div>
            </div>
          </TabsContent>

          <TabsContent value="builder"><VisualBuilder website={w}/></TabsContent>

          <TabsContent value="theme">
            <div className="max-w-2xl bg-white p-6 rounded-xl border border-slate-200 space-y-4">
              <div>
                <Label>Primary color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={w.primary_color} onChange={e => setW({...w, primary_color: e.target.value})} className="h-10 w-14 rounded-lg border cursor-pointer"/>
                  <Input value={w.primary_color} onChange={e => setW({...w, primary_color: e.target.value})} data-testid="primary-color"/>
                </div>
              </div>
              <div>
                <Label>Language</Label>
                <select value={w.language} onChange={e => setW({...w, language: e.target.value})} className="w-full mt-1 h-10 rounded-md border border-slate-300 px-2">
                  <option value="bn">বাংলা</option>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                  <option value="hi">हिन्दी</option>
                </select>
              </div>
              <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg">Current theme: <span className="font-mono font-bold">{w.theme_id}</span></div>
            </div>
          </TabsContent>

          {w.type === "ecommerce" && <TabsContent value="products"><EcommerceManager storeId={w.store_id}/></TabsContent>}
          {w.type === "ecommerce" && <TabsContent value="photocard"><PhotoCard storeId={w.store_id} website={w}/></TabsContent>}
          {(w.type === "news" || w.type === "blog") && <TabsContent value="articles"><NewsManager websiteId={w.website_id}/></TabsContent>}

          <TabsContent value="seo">
            <div className="max-w-2xl bg-white p-6 rounded-xl border border-slate-200 space-y-4">
              <div><Label>Meta title</Label><Input value={w.seo?.title || ""} onChange={e => setW({...w, seo: {...w.seo, title: e.target.value}})} data-testid="seo-title"/></div>
              <div><Label>Meta description</Label><Textarea value={w.seo?.description || ""} onChange={e => setW({...w, seo: {...w.seo, description: e.target.value}})} rows={2}/></div>
              <div><Label>Keywords (comma separated)</Label><Input value={w.seo?.keywords || ""} onChange={e => setW({...w, seo: {...w.seo, keywords: e.target.value}})}/></div>
            </div>
          </TabsContent>

          <TabsContent value="domain"><DomainWizard website={w}/></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
